import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchUsers,
  fetchLastActivityInfo,
  fetchDigitalMenusByBusiness,
  fetchUsesFinanceModule,
  LastActivityInfo,
  DigitalMenuInfo,
} from '../services/firebase';
import { User } from '../types';
import { theme } from '../styles/theme';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CircleIcon from '@mui/icons-material/Circle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HistoryIcon from '@mui/icons-material/History';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { IconButton, Skeleton, Tooltip } from '@mui/material';
import { formatDayLabel } from '../utils/dateFormat';

const ACTIVITY_CACHE_KEY = 'home_activityCache_v2';
const FINANCE_USAGE_CACHE_KEY = 'home_financeUsageCache_v1';
const DIGITAL_MENU_BASE_URL = 'https://meunegocioapp.com.br';

// Cada revalidação de atividade custa ~30 leituras no Firestore POR CLIENTE
// (fetchLastActivityInfo varre 8 coleções × até 5 campos de data, e consulta
// vazia também é cobrada). Com TTL de 10 min e polling de 5 min, uma aba
// esquecida aberta queimava ~3.700 leituras/dia por cliente cadastrado.
// Ver docs/specs/firestore-custo-leituras.md no repo do app Flutter.
const ACTIVITY_CACHE_TTL = 30 * 60 * 1000;
const AUTO_REFRESH_INTERVAL = 30 * 60 * 1000;

// Mesma lógica de custo vale para o indicador de uso do Financeiro: cada
// cliente exige 1-2 leituras em `finance-{uid}` que não dá para agrupar,
// porque as coleções são por lojista.
const FINANCE_USAGE_CACHE_TTL = 30 * 60 * 1000;

// Apenas para o Deploy
type CacheEntry<T> = {
  value: T;
  fetchedAt: number;
};

type TimedCache<T> = Record<string, CacheEntry<T>>;

const readTimedCache = <T,>(key: string): TimedCache<T> => {
  try {
    const rawCache = localStorage.getItem(key);
    return rawCache ? JSON.parse(rawCache) : {};
  } catch {
    return {};
  }
};

const writeTimedCache = <T,>(key: string, cache: TimedCache<T>) => {
  try {
    localStorage.setItem(key, JSON.stringify(cache));
  } catch {
    // Ignora falhas de armazenamento local.
  }
};

const normalizeActivityValue = (value: LastActivityInfo | string | null | undefined): LastActivityInfo | null => {
  if (!value) return null;

  if (typeof value === 'string') {
    return {
      date: value,
      type: 'Atividade registrada',
    };
  }

  if (value.date) {
    return {
      date: value.date,
      type: value.type || 'Atividade registrada',
    };
  }

  return null;
};

type ModuleBadgeState = 'active' | 'partial' | 'inactive';

type ModuleFilter = 'all' | 'menu' | 'finance' | 'both' | 'none';

/**
 * Selo de módulo no card do cliente: colorido quando ele usa o módulo,
 * âmbar quando existe mas está desligado e apagado quando não usa.
 */
const ModuleBadge: React.FC<{
  label: string;
  state: ModuleBadgeState;
  children: React.ReactElement;
  onClick?: (event: React.MouseEvent) => void;
}> = ({ label, state, children, onClick }) => (
  <Tooltip title={label} arrow>
    <span
      style={{
        ...styles.moduleBadge,
        ...(state === 'active'
          ? styles.moduleBadgeActive
          : state === 'partial'
            ? styles.moduleBadgePartial
            : styles.moduleBadgeInactive),
        ...(onClick ? styles.moduleBadgeClickable : null),
      }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-label={label}
    >
      {children}
    </span>
  </Tooltip>
);

const Home: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('home_searchTerm') ?? '');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired'>(
    () => (localStorage.getItem('home_statusFilter') as 'all' | 'active' | 'expired') ?? 'all'
  );
  const [sortBy, setSortBy] = useState<'name' | 'activationDate' | 'expirationDate' | 'sales' | 'activity'>(
    () => (localStorage.getItem('home_sortBy') as 'name' | 'activationDate' | 'expirationDate' | 'sales' | 'activity') ?? 'name'
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    () => (localStorage.getItem('home_sortOrder') as 'asc' | 'desc') ?? 'asc'
  );
  const [moduleFilter, setModuleFilter] = useState<ModuleFilter>(
    () => (localStorage.getItem('home_moduleFilter') as ModuleFilter) ?? 'all'
  );
  const [lastActivityMap, setLastActivityMap] = useState<Record<string, LastActivityInfo | null>>(() => {
    const cache = readTimedCache<LastActivityInfo | string | null>(ACTIVITY_CACHE_KEY);
    const now = Date.now();

    return Object.entries(cache).reduce<Record<string, LastActivityInfo | null>>((acc, [uid, entry]) => {
      if (now - entry.fetchedAt <= ACTIVITY_CACHE_TTL) {
        acc[uid] = normalizeActivityValue(entry.value);
      }
      return acc;
    }, {});
  });
  const [loadingActivityUids, setLoadingActivityUids] = useState<Set<string>>(new Set());
  // null enquanto a varredura de `digitalMenus` não voltou, para o selo não
  // piscar "não utiliza" em todo mundo no primeiro render.
  const [digitalMenuMap, setDigitalMenuMap] = useState<Record<string, DigitalMenuInfo> | null>(null);
  const [financeUsageMap, setFinanceUsageMap] = useState<Record<string, boolean>>(() => {
    const cache = readTimedCache<boolean>(FINANCE_USAGE_CACHE_KEY);
    const now = Date.now();

    return Object.entries(cache).reduce<Record<string, boolean>>((acc, [uid, entry]) => {
      if (now - entry.fetchedAt <= FINANCE_USAGE_CACHE_TTL) {
        acc[uid] = entry.value === true;
      }
      return acc;
    }, {});
  });
  const navigate = useNavigate();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('home_searchTerm', searchTerm);
    localStorage.setItem('home_statusFilter', statusFilter);
    localStorage.setItem('home_sortBy', sortBy);
    localStorage.setItem('home_sortOrder', sortOrder);
    localStorage.setItem('home_moduleFilter', moduleFilter);
  }, [searchTerm, statusFilter, sortBy, sortOrder, moduleFilter]);

  const loadLastActivities = useCallback(async (userList: User[], forceRefresh = false) => {
    const cache = readTimedCache<LastActivityInfo | string | null>(ACTIVITY_CACHE_KEY);
    const now = Date.now();
    const nextMap: Record<string, LastActivityInfo | null> = {};
    const usersToFetch: User[] = [];

    for (const user of userList) {
      const uid = user.uid || user.id;
      const cachedEntry = cache[uid];

      if (!forceRefresh && cachedEntry && now - cachedEntry.fetchedAt <= ACTIVITY_CACHE_TTL) {
        nextMap[uid] = normalizeActivityValue(cachedEntry.value);
      } else {
        usersToFetch.push(user);
      }
    }

    if (Object.keys(nextMap).length > 0) {
      setLastActivityMap(prev => ({ ...prev, ...nextMap }));
    }

    if (usersToFetch.length === 0) {
      return;
    }

    const updatedCache = { ...cache };
    const BATCH_SIZE = 10;
    for (let i = 0; i < usersToFetch.length; i += BATCH_SIZE) {
      const batch = usersToFetch.slice(i, i + BATCH_SIZE);
      const uids = batch.map(u => u.uid || u.id);
      setLoadingActivityUids(prev => new Set([...Array.from(prev), ...uids]));
      const results = await Promise.all(
        batch.map(async (user) => {
          const uid = user.uid || user.id;
          const activity = await fetchLastActivityInfo(uid);
          return { uid, activity };
        })
      );

      const batchMap: Record<string, LastActivityInfo | null> = {};

      results.forEach(({ uid, activity }) => {
        batchMap[uid] = activity;
        updatedCache[uid] = {
          value: activity,
          fetchedAt: Date.now(),
        };
      });

      setLastActivityMap(prev => ({ ...prev, ...batchMap }));
      setLoadingActivityUids(prev => {
        const next = new Set(Array.from(prev));
        results.forEach(({ uid }) => next.delete(uid));
        return next;
      });

      writeTimedCache(ACTIVITY_CACHE_KEY, updatedCache);
    }
  }, []);

  const loadFinanceUsage = useCallback(async (userList: User[], forceRefresh = false) => {
    const cache = readTimedCache<boolean>(FINANCE_USAGE_CACHE_KEY);
    const now = Date.now();
    const nextMap: Record<string, boolean> = {};
    const usersToFetch: User[] = [];

    for (const user of userList) {
      const uid = user.uid || user.id;
      const cachedEntry = cache[uid];

      if (!forceRefresh && cachedEntry && now - cachedEntry.fetchedAt <= FINANCE_USAGE_CACHE_TTL) {
        nextMap[uid] = cachedEntry.value === true;
      } else {
        usersToFetch.push(user);
      }
    }

    if (Object.keys(nextMap).length > 0) {
      setFinanceUsageMap(prev => ({ ...prev, ...nextMap }));
    }

    if (usersToFetch.length === 0) {
      return;
    }

    const updatedCache = { ...cache };
    const BATCH_SIZE = 10;
    for (let i = 0; i < usersToFetch.length; i += BATCH_SIZE) {
      const batch = usersToFetch.slice(i, i + BATCH_SIZE);
      const results = await Promise.all(
        batch.map(async (user) => {
          const uid = user.uid || user.id;
          const usesFinance = await fetchUsesFinanceModule(uid);
          return { uid, usesFinance };
        })
      );

      const batchMap: Record<string, boolean> = {};

      results.forEach(({ uid, usesFinance }) => {
        batchMap[uid] = usesFinance;
        updatedCache[uid] = {
          value: usesFinance,
          fetchedAt: Date.now(),
        };
      });

      setFinanceUsageMap(prev => ({ ...prev, ...batchMap }));
      writeTimedCache(FINANCE_USAGE_CACHE_KEY, updatedCache);
    }
  }, []);

  const loadUsers = useCallback(async (silent = false, forceRefresh = false) => {
    if (refreshInFlightRef.current) {
      return;
    }

    refreshInFlightRef.current = true;
    try {
      if (!silent) {
        setRefreshing(true);
      }
      const data = await fetchUsers();
      setUsers(data as User[]);
      Promise.all([
        loadLastActivities(data as User[], forceRefresh),
        loadFinanceUsage(data as User[], forceRefresh),
        // Uma varredura só resolve o cardápio de todos os clientes.
        fetchDigitalMenusByBusiness().then(setDigitalMenuMap),
      ])
        .catch(error => {
          console.error('Erro ao carregar dados complementares dos clientes:', error);
        })
        .finally(() => {
          refreshInFlightRef.current = false;
          setRefreshing(false);
        });
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      refreshInFlightRef.current = false;
      setRefreshing(false);
    } finally {
      setLoading(false);
    }
  }, [loadLastActivities, loadFinanceUsage]);

  useEffect(() => {
    loadUsers();

    // Só atualiza com a aba em primeiro plano. Antes o polling rodava 24h numa
    // aba esquecida aberta, varrendo todos os clientes a cada ciclo sem
    // ninguém olhando. Ao voltar para a aba, o próximo ciclo atualiza — e o
    // botão de refresh continua disponível para forçar na hora.
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      loadUsers(true);
    };

    intervalRef.current = setInterval(tick, AUTO_REFRESH_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loadUsers]);

  const handleRefresh = () => {
    loadUsers(false, true);
  };

  const handleUserClick = (userUid: string) => {
    console.log('Navegando para usuário com UID:', userUid);
    navigate(`/user/${userUid}`);
  };

  const isExpired = (expirationDate: string) => {
    const expDate = new Date(expirationDate);
    const today = new Date();
    return expDate < today;
  };

  const calculateMetrics = () => {
    const total = users.length;
    const active = users.filter(user => !isExpired(user.expirationDate)).length;
    const expired = users.filter(user => isExpired(user.expirationDate)).length;
    return { total, active, expired };
  };

  const filterAndSortUsers = () => {
    let filtered = [...users];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.businessName?.toLowerCase().includes(term) ||
        user.mail?.toLowerCase().includes(term) ||
        user.phone?.toLowerCase().includes(term)
      );
    }

    if (statusFilter === 'active') {
      filtered = filtered.filter(user => !isExpired(user.expirationDate));
    } else if (statusFilter === 'expired') {
      filtered = filtered.filter(user => isExpired(user.expirationDate));
    }

    if (moduleFilter !== 'all') {
      filtered = filtered.filter(user => {
        const uid = user.uid || user.id;
        // Cardápio ainda não verificado conta como "não tem": a lista cresce
        // sozinha conforme os dados dos módulos chegam.
        const hasMenu = Boolean(digitalMenuMap?.[uid]?.slug);
        const usesFinance = financeUsageMap[uid] === true;

        if (moduleFilter === 'menu') return hasMenu;
        if (moduleFilter === 'finance') return usesFinance;
        if (moduleFilter === 'both') return hasMenu && usesFinance;
        return !hasMenu && !usesFinance;
      });
    }

    filtered.sort((a, b) => {
      let comparison = 0;
      const aName = a.businessName || '';
      const bName = b.businessName || '';
      const tieBreaker = aName.localeCompare(bName) || (a.uid || a.id).localeCompare(b.uid || b.id);

      if (sortBy === 'name') {
        comparison = tieBreaker;
      } else if (sortBy === 'activationDate') {
        comparison = new Date(a.activationDate).getTime() - new Date(b.activationDate).getTime();
        if (comparison === 0) {
          comparison = tieBreaker;
        }
      } else if (sortBy === 'expirationDate') {
        comparison = new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime();
        if (comparison === 0) {
          comparison = tieBreaker;
        }
      } else if (sortBy === 'sales') {
        comparison = (a.salesCount || 0) - (b.salesCount || 0);
        if (comparison === 0) {
          comparison = tieBreaker;
        }
      } else if (sortBy === 'activity') {
        const aUid = a.uid || a.id;
        const bUid = b.uid || b.id;
        const aActivity = lastActivityMap[aUid]?.date ? new Date(lastActivityMap[aUid]!.date).getTime() : null;
        const bActivity = lastActivityMap[bUid]?.date ? new Date(lastActivityMap[bUid]!.date).getTime() : null;

        if (aActivity === null && bActivity === null) {
          comparison = tieBreaker;
        } else if (aActivity === null) {
          comparison = 1;
        } else if (bActivity === null) {
          comparison = -1;
        } else {
          comparison = sortOrder === 'asc'
            ? aActivity - bActivity
            : bActivity - aActivity;

          if (comparison === 0) {
            comparison = tieBreaker;
          }
        }

        return comparison;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loading}>Carregando...</div>
      </div>
    );
  }

  const metrics = calculateMetrics();
  const filteredUsers = filterAndSortUsers();
  // Uso do financeiro chega em lotes; com filtro de módulo ligado, o resultado
  // ainda está incompleto enquanto isso.
  const moduleDataPending =
    digitalMenuMap === null ||
    users.some(user => financeUsageMap[user.uid || user.id] === undefined);
  const showModulePendingHint = moduleFilter !== 'all' && moduleDataPending;

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Clientes</h1>
        <IconButton
          style={styles.refreshButton}
          onClick={handleRefresh}
          disabled={refreshing}
          title={refreshing ? 'Atualizando clientes' : 'Atualizar clientes'}
          aria-label={refreshing ? 'Atualizando clientes' : 'Atualizar clientes'}
        >
          <RefreshIcon style={refreshing ? styles.refreshIconSpinning : styles.refreshIcon} />
        </IconButton>
      </header>
      <div style={styles.content}>
        {users.length === 0 ? (
          <p style={styles.emptyMessage}>Nenhum cliente encontrado</p>
        ) : (
          <>
            <div style={styles.metricsContainer}>
              <div style={styles.metricCard}>
                <div style={styles.metricIcon}><PersonIcon sx={{ fontSize: 40, color: theme.colors.primary }} /></div>
                <div style={styles.metricContent}>
                  <div style={styles.metricValue}>{metrics.total}</div>
                  <div style={styles.metricLabel}>Total de Clientes</div>
                </div>
              </div>

              <div style={{...styles.metricCard, ...styles.metricCardActive}}>
                <div style={styles.metricIcon}><PersonIcon sx={{ fontSize: 40, color: '#4caf50' }} /></div>
                <div style={styles.metricContent}>
                  <div style={styles.metricValue}>{metrics.active}</div>
                  <div style={styles.metricLabel}>Clientes Ativos</div>
                </div>
              </div>

              <div style={{...styles.metricCard, ...styles.metricCardExpired}}>
                <div style={styles.metricIcon}><PersonIcon sx={{ fontSize: 40, color: '#f44336' }} /></div>
                <div style={styles.metricContent}>
                  <div style={styles.metricValue}>{metrics.expired}</div>
                  <div style={styles.metricLabel}>Clientes Expirados</div>
                </div>
              </div>
            </div>

            {/* Barra de Filtros e Busca */}
            <div style={styles.filterContainer}>
              <div style={styles.searchBox}>
                <SearchIcon style={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Buscar por nome, email ou telefone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={styles.searchInput}
                />
                {searchTerm && (
                  <button
                    style={styles.clearButton}
                    onClick={() => setSearchTerm('')}
                  >
                    ✕
                  </button>
                )}
              </div>

              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'expired')}
                  style={styles.select}
                >
                  <option value="all">Todos</option>
                  <option value="active">Ativos</option>
                  <option value="expired">Expirados</option>
                </select>
              </div>

              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Módulos:</label>
                <select
                  value={moduleFilter}
                  onChange={(e) => setModuleFilter(e.target.value as ModuleFilter)}
                  style={styles.select}
                >
                  <option value="all">Todos</option>
                  <option value="menu">Com cardápio</option>
                  <option value="finance">Com financeiro</option>
                  <option value="both">Com os dois</option>
                  <option value="none">Sem nenhum</option>
                </select>
              </div>

              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'activationDate' | 'expirationDate' | 'sales' | 'activity')}
                  style={styles.select}
                >
                  <option value="name">Nome</option>
                  <option value="activationDate">Data de Ativação</option>
                  <option value="expirationDate">Data de Expiração</option>
                  <option value="sales">Número de Vendas</option>
                  <option value="activity">Atividade</option>
                </select>
              </div>

              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Ordem:</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  style={styles.select}
                >
                  <option value="asc">
                    {sortBy === 'name' ? 'A → Z' :
                     sortBy === 'sales' ? 'Menor → Maior' :
                    sortBy === 'activity' ? 'Mais antiga → Recente' :
                     sortBy === 'activationDate' ? 'Mais antiga → Recente' :
                     'Mais próxima → Distante'}
                  </option>
                  <option value="desc">
                    {sortBy === 'name' ? 'Z → A' :
                     sortBy === 'sales' ? 'Maior → Menor' :
                    sortBy === 'activity' ? 'Recente → Mais antiga' :
                     sortBy === 'activationDate' ? 'Recente → Mais antiga' :
                     'Distante → Mais próxima'}
                  </option>
                </select>
              </div>
            </div>

            {filteredUsers.length === 0 ? (
              <p style={styles.emptyMessage}>
                {showModulePendingHint
                  ? 'Verificando os módulos dos clientes...'
                  : 'Nenhum cliente encontrado com os filtros aplicados'}
              </p>
            ) : (
              <div style={styles.resultCount}>
                Mostrando {filteredUsers.length} de {users.length} cliente(s)
                {showModulePendingHint && ' • ainda verificando os módulos'}
              </div>
            )}

            <div style={styles.grid}>
              {filteredUsers.map((user) => {
                const expired = isExpired(user.expirationDate);
                const uid = user.uid || user.id;

                const menu = digitalMenuMap?.[uid];
                const menuUrl = menu?.slug ? `${DIGITAL_MENU_BASE_URL}/${menu.slug}/cardapio` : null;
                const menuState: ModuleBadgeState = !menu?.slug
                  ? 'inactive'
                  : menu.active
                    ? 'active'
                    : 'partial';
                const menuLabel = !menu?.slug
                  ? 'Cardápio/Catálogo digital: não utiliza'
                  : menu.active
                    ? `Cardápio/Catálogo digital ativo — abrir /${menu.slug}/cardapio`
                    : `Cardápio/Catálogo digital criado, mas desativado — abrir /${menu.slug}/cardapio`;

                const usesFinance = financeUsageMap[uid];
                const financeLabel = usesFinance
                  ? 'Financeiro: usa (tem despesa ou receita fora de venda/OS)'
                  : 'Financeiro: não utiliza (só lançamentos de venda/OS)';

                return (
                  <div
                    key={uid}
                    style={styles.card}
                    onClick={() => handleUserClick(uid)}
                  >
                    <div style={styles.cardHeader}>
                      {user.photoUrl ? (
                        <img src={user.photoUrl} alt={user.businessName} style={styles.userPhoto} />
                      ) : (
                        <div style={styles.userPhotoPlaceholder}>
                          {user.businessName?.charAt(0).toUpperCase() || 'U'}
                        </div>
                      )}
                      <div style={styles.cardHeaderText}>
                        <h3 style={styles.userName}>{user.businessName || 'Nome não informado'}</h3>
                        <div style={styles.statusRow}>
                          <span style={expired ? styles.statusExpired : styles.statusActive}>
                            <CircleIcon style={{ fontSize: 12, marginRight: 6 }} />
                            {expired ? 'Expirado' : 'Ativo'}
                          </span>
                          <div style={styles.moduleBadges}>
                            {digitalMenuMap === null ? (
                              <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: '8px' }} />
                            ) : (
                              <ModuleBadge
                                label={menuLabel}
                                state={menuState}
                                onClick={
                                  menuUrl
                                    ? (event) => {
                                        // O card inteiro navega para o detalhe do cliente.
                                        event.stopPropagation();
                                        window.open(menuUrl, '_blank', 'noopener,noreferrer');
                                      }
                                    : undefined
                                }
                              >
                                <MenuBookIcon style={styles.moduleIcon} />
                              </ModuleBadge>
                            )}
                            {usesFinance === undefined ? (
                              <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: '8px' }} />
                            ) : (
                              <ModuleBadge label={financeLabel} state={usesFinance ? 'active' : 'inactive'}>
                                <AccountBalanceWalletIcon style={styles.moduleIcon} />
                              </ModuleBadge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={styles.cardBody}>
                      <p style={styles.userInfo}>
                        <EmailIcon style={styles.infoIcon} />
                        {user.mail || 'Email não informado'}
                      </p>
                      <p style={styles.userInfo}>
                        <PhoneIcon style={styles.infoIcon} />
                        {user.phone || 'Telefone não informado'}
                      </p>
                      <div style={styles.cardActivity}>
                        <HistoryIcon style={styles.activityIcon} />
                        {loadingActivityUids.has(uid) && lastActivityMap[uid] === undefined ? (
                          <Skeleton variant="text" width={100} height={18} sx={{ borderRadius: '4px' }} />
                        ) : lastActivityMap[uid] !== undefined ? (
                          <span style={styles.activityText}>
                            {lastActivityMap[uid]?.date
                              ? `${formatDayLabel(lastActivityMap[uid]!.date)}${lastActivityMap[uid]?.type ? ` • ${lastActivityMap[uid]!.type}` : ''}`
                              : 'Sem atividade'}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
  },
  header: {
    background: theme.colors.white,
    padding: '25px 40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  title: {
    color: theme.colors.primary,
    margin: 0,
    fontSize: '28px',
    fontWeight: 'bold',
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    background: theme.gradient,
    color: theme.colors.white,
    transition: 'all 0.3s',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  refreshIcon: {
    fontSize: '18px',
    display: 'inline-block',
  },
  refreshIconSpinning: {
    fontSize: '18px',
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
  },
  content: {
    padding: '40px',
  },
  metricsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
  },
  metricCard: {
    background: theme.colors.white,
    padding: '25px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    borderLeft: `4px solid ${theme.colors.primary}`,
    transition: 'transform 0.3s',
  },
  metricCardActive: {
    borderLeft: '4px solid #4caf50',
  },
  metricCardExpired: {
    borderLeft: '4px solid #f44336',
  },
  metricIcon: {
    fontSize: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  metricValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  metricLabel: {
    fontSize: '14px',
    color: theme.colors.text,
    fontWeight: '500',
  },
  filterContainer: {
    background: theme.colors.white,
    padding: '25px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    marginBottom: '30px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    alignItems: 'center',
  },
  searchBox: {
    flex: '1 1 180px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: '15px',
    fontSize: '18px',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '12px 45px 12px 45px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.tertiary}`,
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.3s',
  },
  clearButton: {
    position: 'absolute',
    right: '10px',
    background: 'transparent',
    border: 'none',
    fontSize: '20px',
    color: theme.colors.text,
    cursor: 'pointer',
    padding: '5px 10px',
    borderRadius: '50%',
    transition: 'background 0.3s',
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: theme.colors.text,
    whiteSpace: 'nowrap',
  },
  select: {
    padding: '10px 15px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.tertiary}`,
    fontSize: '14px',
    fontWeight: '500',
    color: theme.colors.text,
    background: theme.colors.white,
    cursor: 'pointer',
    outline: 'none',
    minWidth: '180px',
  },
  resultCount: {
    fontSize: '14px',
    color: theme.colors.secondary,
    marginBottom: '15px',
    fontWeight: '500',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '20px',
  },
  card: {
    background: theme.colors.white,
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    transition: 'all 0.3s',
    borderLeft: `4px solid ${theme.colors.primary}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '15px',
    paddingBottom: '15px',
    borderBottom: '1px solid #e0e0e0',
  },
  cardHeaderText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  userPhoto: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: `3px solid ${theme.colors.tertiary}`,
  },
  userPhotoPlaceholder: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: theme.gradient,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '24px',
    color: theme.colors.white,
    fontWeight: 'bold',
  },
  userName: {
    color: theme.colors.primary,
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold',
  },
  statusActive: {
    display: 'inline-block',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#4caf50',
  },
  statusExpired: {
    display: 'inline-block',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#f44336',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
  moduleBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
  },
  moduleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    transition: 'background 0.2s',
  },
  moduleBadgeActive: {
    background: 'rgba(13, 71, 161, 0.12)',
    color: theme.colors.primary,
  },
  moduleBadgePartial: {
    background: 'rgba(245, 158, 11, 0.18)',
    color: '#b45309',
  },
  moduleBadgeInactive: {
    background: '#f2f4f7',
    color: '#c3cad3',
  },
  moduleBadgeClickable: {
    cursor: 'pointer',
  },
  moduleIcon: {
    fontSize: '17px',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  userInfo: {
    color: theme.colors.text,
    margin: 0,
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  infoIcon: {
    fontSize: '16px',
  },
  cardActivity: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    paddingTop: '8px',
    borderTop: '1px solid #f0f0f0',
  },
  activityIcon: {
    fontSize: '16px',
    color: theme.colors.primary,
    flexShrink: 0,
  },
  activityText: {
    fontSize: '13px',
    color: '#888',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: theme.gradient,
  },
  loading: {
    color: theme.colors.white,
    fontSize: '24px',
    fontWeight: 'bold',
  },
  emptyMessage: {
    textAlign: 'center',
    color: theme.colors.text,
    fontSize: '18px',
    marginTop: '40px',
  },
};

export default Home;
